import type { AuthConfig } from "./types"

const DEFAULT_SCOPE = "openid profile email"
/**
 * Where a sign-in lands when no destination was requested. This is the service
 * launcher, not a content section — the launcher became the dashboard's entry
 * point and this had been left pointing at the old default.
 */
export const DEFAULT_AUTHENTICATED_ROUTE = "/dashboard"

export function getAuthConfig(): AuthConfig {
  const domain = readEnv("AUTH0_DOMAIN", "VITE_AUTH0_DOMAIN")
  const clientId = readEnv("AUTH0_CLIENT_ID", "VITE_AUTH0_CLIENT_ID")
  const dbConnection = readEnv("AUTH0_DB_CONNECTION", "VITE_AUTH0_DB_CONNECTION")
  const audience = readEnv("AUTH0_AUDIENCE", "VITE_AUTH0_AUDIENCE")
  const scope = readEnv("AUTH0_SCOPE", "VITE_AUTH0_SCOPE") ?? DEFAULT_SCOPE
  const isConfigured = Boolean(domain && clientId && dbConnection)

  return {
    domain: domain ?? "",
    clientId: clientId ?? "",
    dbConnection: dbConnection ?? "",
    audience,
    scope,
    isConfigured,
  }
}

function readEnv(...keys: string[]): string | undefined {
  for (const key of keys) {
    if (typeof process !== "undefined" && process.env && Object.hasOwn(process.env, key)) {
      const processValue = readEnvValue(process.env[key])
      if (processValue) return processValue
      continue
    }
    const value = readEnvValue(import.meta.env?.[key])
    if (value) return value
  }
  return undefined
}

export function readEnvValue(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined
  const trimmed = value.trim()
  if (!trimmed) return undefined
  if (
    (trimmed.startsWith('"') && trimmed.endsWith('"')) ||
    (trimmed.startsWith("'") && trimmed.endsWith("'"))
  ) {
    const unquoted = trimmed.slice(1, -1).trim()
    return unquoted || undefined
  }
  return trimmed
}

export function assertConfigured(config: AuthConfig) {
  if (!config.isConfigured) {
    throw new Error(
      "Dashboard Auth0 is not configured. Set AUTH0_DOMAIN, AUTH0_CLIENT_ID, and AUTH0_DB_CONNECTION."
    )
  }
}

export function isLocalDevStage() {
  const stage = readEnv("PUBLIC_STAGE", "VITE_PUBLIC_STAGE")
  if (!stage || stage === "local") return true
  if (stage !== "dev") return false
  if (typeof window === "undefined") return true
  return window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1"
}

export function isDashboardAuthBypassEnabled() {
  return isLocalDevStage() && readBooleanEnv("DASHBOARD_AUTH_BYPASS", "VITE_DASHBOARD_AUTH_BYPASS")
}

export function currentOrigin() {
  if (typeof window !== "undefined") return window.location.origin
  return "http://localhost:3000"
}

function readBooleanEnv(...keys: string[]) {
  const value = readEnv(...keys)
  if (!value) return false
  return ["1", "true", "yes", "on"].includes(value.toLowerCase())
}
